import { Button, ColorOption, Spinner, StandardTabs } from '@cimplico/components';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../providers/FirmProvider';
import { useGroup } from '../queries/use-group';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import ErrorSplashScreen from './ErrorSplashScreen';
import PageContainer from '../components/page-container/PageContainer';
import EntitiesSection from '../features/group-overview/EntitiesSection';
import GroupDetails from '../features/group-overview/GroupDetails';
import WorkpapersSection from '../features/group-overview/WorkpapersSection';
import GroupsDeleteConfirm from '../features/groups/GroupsDeleteConfirm';
import FilesSection from '../features/files/FilesSection';
import GroupIntegrations from '../features/group-overview/GroupIntegrations';
import GroupOverviewFirmInsights from '../features/group-overview/firm-insights/GroupOverviewFirmInsights';

function randomColor(): ColorOption {
    const colors = ['gray', 'red', 'yellow', 'green', 'blue', 'violet', 'indigo', 'pink', 'orange'];
    return colors[Math.floor(Math.random() * colors.length)] as ColorOption;
}

const tabs = ['insights', 'entities', 'workpapers', 'files', 'settings'];

function getSavedTab(isExternalUser = false, tab: string | null): number {
    if (!tab || (isExternalUser && tab === 'integrations')) {
        return 0;
    }

    return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
}

function GroupOverviewPage(): JSX.Element {
    const navigate = useNavigate();
    const { isExternalUser } = useIsFirmRole();
    const [params, setParams] = useSearchParams();
    const [currentTab, setCurrentTab] = useState(getSavedTab(isExternalUser, params.get('tab')));
    const [color] = useState(randomColor());
    const { id } = useParams();
    const { shortId } = useFirmContext();
    const activeSubscription = useIsActiveSubscription();

    const [isDelete, setIsDelete] = useState<boolean>(false);

    const groupQuery = useGroup(id);

    function handleTabChange(t: number): void {
        setCurrentTab(t);
        setParams({ tab: tabs[t] });
    }

    if (groupQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (groupQuery.isError || !groupQuery.isSuccess) {
        if (groupQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Group not found."
                    redirectTitle="Back to groups"
                    redirectUrl={`/${shortId}/groups`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to groups"
                redirectUrl={`/${shortId}/groups`}
                leftArrow={true}
            />
        );
    }

    const { group } = groupQuery.data;

    return (
        <PageContainer>
            {groupQuery.isFetching && (
                <div className="pointer-events-none absolute top-[var(--navbar-height)] left-0 w-full h-[var(--remaining-screen-height)] z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <GroupsDeleteConfirm
                groups={[group]}
                isOpen={isDelete}
                onClose={(): void => setIsDelete(false)}
                onDelete={(): void => {
                    navigate(`/${shortId}/groups`);
                }}
            />
            <div className="grid" style={{ gridTemplateColumns: 'auto min-content' }}>
                <div className="pr-4">
                    {/* DETAILS */}
                    <div className="flex justify-between items-start">
                        <GroupDetails group={group} color={color} />
                        <div className="flex gap-4">
                            <Button
                                type="secondary"
                                label="Edit Details"
                                onClick={(): void => {
                                    navigate('edit');
                                }}
                                disabled={!activeSubscription || isExternalUser}
                            />
                            <Button
                                type="danger"
                                label="Delete"
                                onClick={(): void => setIsDelete(true)}
                                disabled={!activeSubscription || isExternalUser}
                            />
                        </div>
                    </div>

                    {/* TABS */}
                    <section className="mt-8">
                        <StandardTabs
                            tabs={
                                isExternalUser
                                    ? [{ label: 'Insights' }, { label: 'Entities' }, { label: 'Workpapers' }, { label: 'Files' }]
                                    : [
                                          { label: 'Insights' },
                                          { label: 'Entities' },
                                          { label: 'Workpapers' },
                                          { label: 'Files' },
                                          { label: 'Integrations' },
                                      ]
                            }
                            onChange={handleTabChange}
                            defaultIndex={currentTab}
                        />
                    </section>
                    <section className="pt-4 pb-2 min-h-[400px] flex flex-col">
                        {isExternalUser ? (
                            <>
                                {currentTab === 0 && <GroupOverviewFirmInsights />}
                                {currentTab === 1 && <EntitiesSection groupName={group.name} />}
                                {currentTab === 2 && <WorkpapersSection groupId={group.id} />}
                                {currentTab === 3 && <FilesSection startingFolderId={group.id} readOnly={!activeSubscription} />}
                            </>
                        ) : (
                            <>
                                {currentTab === 0 && <GroupOverviewFirmInsights />}
                                {currentTab === 1 && (
                                    <EntitiesSection
                                        groupName={group.name}
                                        fyiGroupName={group.integrations.fyi?.name}
                                        fyiGroupId={group.integrations.fyi?.id}
                                    />
                                )}
                                {currentTab === 2 && <WorkpapersSection groupId={group.id} />}
                                {currentTab === 3 && <FilesSection startingFolderId={group.id} readOnly={!activeSubscription} />}
                                {currentTab === 4 && <GroupIntegrations group={group} />}
                            </>
                        )}
                    </section>
                </div>
            </div>
        </PageContainer>
    );
}
export default GroupOverviewPage;
